import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";
import { Mail, ArrowLeft, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t("reset_link_sent"));
    } catch (err) {
      // Always show success-style message — endpoint never reveals existence
      setSent(true);
      toast.success(t("reset_link_sent"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 py-16" data-testid="forgot-password-page" dir={ar ? "rtl" : "ltr"}>
      <div className="flex flex-col items-center gap-3 mb-8">
        <img
          src="/logo192.png"
          alt="ثقة"
          className="h-20 w-20 rounded-full object-cover border border-[#D4AF37]/40 shadow-[0_0_24px_-4px_rgba(212,175,55,0.5)]"
        />
        <h1 className="text-2xl font-black gold-text text-center">{t("forgot_password_title")}</h1>
        <p className="text-white/60 text-sm text-center max-w-xs">{t("forgot_password_intro")}</p>
      </div>

      {!sent ? (
        <form onSubmit={submit} className="glass rounded-2xl p-7 space-y-4">
          <div>
            <label className="text-xs text-white/70 uppercase tracking-wider">{t("email")}</label>
            <div className="mt-1 relative">
              <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#D4AF37]" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none rounded-md ps-9 pe-3 py-2.5 text-sm"
                data-testid="forgot-email"
              />
            </div>
          </div>
          <button
            disabled={loading}
            type="submit"
            className="btn-gold w-full rounded-md py-3 font-bold disabled:opacity-50"
            data-testid="forgot-submit"
          >
            {loading ? t("loading") : t("send_reset_link")}
          </button>

          <Link
            to="/login"
            className="block text-center text-sm text-white/65 hover:text-[#D4AF37] inline-flex items-center justify-center gap-1 w-full"
            data-testid="back-to-login"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("back_to_login")}
          </Link>
        </form>
      ) : (
        <div className="glass-gold rounded-2xl p-7 border border-[#D4AF37]/40 text-center space-y-4" data-testid="forgot-success">
          <div className="h-14 w-14 rounded-full mx-auto inline-flex items-center justify-center bg-emerald-500/15 border border-emerald-500/40">
            <ShieldCheck className="h-7 w-7 text-emerald-400" />
          </div>
          <p className="text-white/85 text-sm leading-relaxed">{t("reset_link_sent")}</p>
          <Link to="/login" className="btn-gold-outline rounded-full px-5 py-2 text-sm inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("back_to_login")}
          </Link>
        </div>
      )}
    </div>
  );
}
